import React, { useContext, useEffect, useReducer, useState } from "react";
import clsx from "clsx";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import {
  LayoutDashboard as DashboardOutlinedIcon,
  MessageCircle as WhatsAppIcon,
  RefreshCw as SyncAltIcon,
  Settings as SettingsOutlinedIcon,
  Users as PeopleAltOutlinedIcon,
  Contact as ContactPhoneOutlinedIcon,
  GitBranch as AccountTreeOutlinedIcon,
  Zap as FlashOnIcon,
  HelpCircle as HelpOutlineIcon,
  Code2 as CodeRoundedIcon,
  CalendarClock as Schedule,
  Tags as LocalOfferIcon,
  CalendarCheck as EventAvailableIcon,
  ChevronUp as ExpandLessIcon,
  ChevronDown as ExpandMoreIcon,
  Users as PeopleIcon,
  List as ListIcon,
  Megaphone as AnnouncementIcon,
  MessageSquare as ForumIcon,
  Banknote as LocalAtmIcon,
  Building2 as BusinessIcon,
  Infinity as AllInclusive,
  Paperclip as AttachFile,
  LayoutDashboard as Dashboard,
  FileText as Description,
  Router as DeviceHubOutlined,
  Grid3x3 as GridOn,
  List as ListAlt,
  Smartphone as PhonelinkSetup,
  Cpu as Memory,
  SquareKanban as ViewKanban,
  Megaphone as Campaign,
  Workflow as ShapeLine,
  Webhook as Webhook,
  Bot as SmartToy,
  ShieldCheck as RolesIcon,
  UserPlus as LeadsImportIcon,
  ScanSearch as LeadScraperIcon,
  BookOpen as KnowledgeBaseIcon,
  Briefcase as DealsIcon,
  Sliders as CustomFieldsIcon,
} from "lucide-react";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";
import usePermissions from "../hooks/usePermissions";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";

import ColorModeContext from "./themeContext";

const useStyles = makeStyles((theme) => ({
  modernItem: {
    margin: "4px 12px",
    borderRadius: "12px",
    width: "calc(100% - 24px)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "var(--primary-bg-fade) !important",
      transform: "translateX(4px)",
    },
    "&.active": {
      backgroundColor: "var(--primary-bg-fade) !important",
      "& $modernIcon": {
        color: "var(--primary-color) !important",
        filter: "drop-shadow(0 0 8px var(--primary-glow))",
      },
      "& $modernText": {
        color: "var(--primary-color) !important",
        fontWeight: "600 !important",
      }
    }
  },
  modernIcon: {
    color: "var(--text-muted) !important",
    minWidth: "40px !important",
    transition: "all 0.3s ease",
  },
  modernText: {
    color: "var(--text) !important",
    fontFamily: "'Plus Jakarta Sans', sans-serif !important",
    fontSize: "0.875rem !important",
  },
  listItem: {
    height: "44px",
    width: "auto",
    "&:hover $iconHoverActive": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },

  listItemText: {
    fontSize: "14px",
    color: theme.mode === "light" ? "#666" : "#FFF",
  },
  avatarActive: {
    backgroundColor: "transparent",
  },
  avatarHover: {
    backgroundColor: "transparent",
  },
  iconHoverActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    height: 36,
    width: 36,
    backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
    color: theme.mode === "light" ? "#666" : "#FFF",
    // color: theme.mode === "light" ? theme.palette.primary.main : "#FFF",
    "&:hover, &.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
    "& .MuiSvgIcon-root, & .lucide": {
      fontSize: "1.4rem",
      width: "1.4rem",
      height: "1.4rem",
    },
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, viewMode } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () => React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
    [to]
  );

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip title={primary} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem
          button
          component={renderLink}
          className={clsx(classes.listItem, viewMode === "modern" && classes.modernItem, isActive && "active")}
        >
          {icon ? (
            <ListItemIcon className={clsx(viewMode === "modern" && classes.modernIcon)}>
              {showBadge ? (
                <Badge badgeContent="!" color="error" overlap="circular" className={classes.badge}>
                  {viewMode === "modern" ? icon : <Avatar className={`${classes.iconHoverActive} ${isActive ? "active" : ""}`}>{icon}</Avatar>}
                </Badge>
              ) : (
                viewMode === "modern" ? icon : <Avatar className={`${classes.iconHoverActive} ${isActive ? "active" : ""}`}>{icon}</Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText primary={<Typography className={clsx(classes.listItemText, viewMode === "modern" && classes.modernText)}>{primary}</Typography>} />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const classes = useStyles();
  const { colorMode } = useContext(ColorModeContext);
  const { viewMode } = colorMode;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();
  const { hasPermission, hasAnyPermission, isAdmin } = usePermissions();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  const [openDashboardSubmenu, setOpenDashboardSubmenu] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [versionInfo, setVersionInfo] = useState({ frontend: "", backend: "", commit: "", commitShort: "", buildDate: "" });
  const [managementHover, setManagementHover] = useState(false);
  const [campaignHover, setCampaignHover] = useState(false);
  const [flowHover, setFlowHover] = useState(false);
  const [openIASubmenu, setOpenIASubmenu] = useState(false);
  const [iaHover, setIaHover] = useState(false);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);


  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  const isManagementActive =
    location.pathname === "/" || location.pathname.startsWith("/reports") || location.pathname.startsWith("/moments");

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config");

  const isFlowbuilderRouteActive =
    location.pathname.startsWith("/phrase-lists") ||
    location.pathname.startsWith("/flowbuilders");

  const isIARouteActive =
    location.pathname === "/ai-agents" ||
    location.pathname === "/prompts" ||
    location.pathname === "/ai-settings" ||
    location.pathname === "/ai-training" ||
    location.pathname === "/files";

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const data = await getVersion();
      const frontendVersion = data?.version || "N/A";
      const backendVersion = data?.backend?.version || "N/A";
      // backendLabel não será usado na UI (exibiremos apenas a versão pura do backend)
      const commit = data?.backend?.commit || "N/A";
      const commitShort = data?.backend?.commitShort || (commit && commit.length >= 6 ? commit.substring(0, 6) : commit);
      const buildDateRaw = data?.backend?.buildDate || "N/A";
      let buildDate = buildDateRaw;
      try {
        const d = new Date(buildDateRaw);
        if (!isNaN(d.getTime())) {
          buildDate = d.toLocaleString();
        }
      } catch (e) {
        // ignore parse errors, keep raw string
      }
      setVersionInfo({ frontend: frontendVersion, backend: backendVersion, commit, commitShort, buildDate });
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id) {
      const companyId = user.companyId;
      //    const socket = socketManager.GetSocket();
      // console.log('socket nListItems')
      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatMainListItems);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatMainListItems);
      };
    }
  }, [socket]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div onClick={drawerClose}>
      {/* 1. DASHBOARD */}
      {hasPermission("dashboard.view") && (
        <ListItemLink
          to="/"
          primary="Dashboard"
          icon={<DashboardOutlinedIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 2. ATENDIMENTOS */}
      {hasPermission("tickets.view") && (
        <ListItemLink
          to="/tickets"
          primary={i18n.t("mainDrawer.listItems.tickets")}
          icon={<WhatsAppIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 3. GRUPOS */}
      {hasPermission("contacts.view") && (
        <ListItemLink
          to="/groups"
          primary="Grupos"
          icon={<PeopleIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 4. CHAT INTERNO */}
      {showInternalChat && hasPermission("internal-chat.view") && (
        <ListItemLink
          to="/chats"
          primary={i18n.t("mainDrawer.listItems.chats")}
          icon={
            <Badge color="secondary" variant="dot" invisible={invisible} overlap="rectangular">
              <ForumIcon />
            </Badge>
          }
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 5. CONTATOS */}
      {hasPermission("contacts.view") && (
        <ListItemLink
          to="/contacts"
          primary={i18n.t("mainDrawer.listItems.contacts")}
          icon={<ContactPhoneOutlinedIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 6. PAINEL (MOMENTS) */}
      {hasPermission("realtime.view") && (
        <ListItemLink
          to="/moments"
          primary={i18n.t("mainDrawer.listItems.chatsTempoReal")}
          icon={<GridOn />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 7. KANBAN */}
      {showKanban && hasPermission("kanban.view") && (
        <ListItemLink
          to="/kanban"
          primary={i18n.t("mainDrawer.listItems.kanban")}
          icon={<ViewKanban />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* NEGÓCIOS / DEALS */}
      <ListItemLink
        to="/deals"
        primary="Negócios"
        icon={<DealsIcon />}
        viewMode={viewMode}
        tooltip={collapsed}
      />

      {/* 8. RESPOSTAS RÁPIDAS */}
      {hasPermission("quick-messages.view") && (
        <ListItemLink
          to="/quick-messages"
          primary={i18n.t("mainDrawer.listItems.quickMessages")}
          icon={<FlashOnIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 9. AGENDAMENTOS */}
      {showSchedules && hasPermission("schedules.view") && (
        <ListItemLink
          to="/schedules"
          primary={i18n.t("mainDrawer.listItems.schedules")}
          icon={<Schedule />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 10. TAGS */}
      {hasPermission("tags.view") && (
        <ListItemLink
          to="/tags"
          primary={i18n.t("mainDrawer.listItems.tags")}
          icon={<LocalOfferIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 11. AJUDA */}
      {hasPermission("helps.view") && (
        <ListItemLink
          to="/helps"
          primary={i18n.t("mainDrawer.listItems.helps")}
          icon={<HelpOutlineIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      <Divider />
      <ListSubheader inset>{i18n.t("mainDrawer.listItems.administration")}</ListSubheader>

      {/* 1. USUÁRIOS */}
      {hasPermission("users.view") && (
        <ListItemLink
          to="/users"
          primary={i18n.t("mainDrawer.listItems.users")}
          icon={<PeopleAltOutlinedIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 1.1 PERFIS DE ACESSO (ROLES) */}
      {hasPermission("roles.view") && (
        <ListItemLink
          to="/roles"
          primary="Perfis de Acesso"
          icon={<RolesIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* CAMPOS CUSTOMIZADOS (ADMIN) */}
      <ListItemLink
        to="/admin-custom-fields"
        primary="Campos Customizados"
        icon={<CustomFieldsIcon />}
        viewMode={viewMode}
        tooltip={collapsed}
      />

      {/* 2. CONEXÕES */}
      {hasPermission("connections.view") && (
        <ListItemLink
          to="/connections"
          primary={i18n.t("mainDrawer.listItems.connections")}
          icon={<SyncAltIcon />}
          showBadge={connectionWarning}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 3. GERENCIAR CONEXÕES (SUPER) */}
      {user.super && (
        <ListItemLink
          to="/allConnections"
          primary={i18n.t("mainDrawer.listItems.allConnections")}
          icon={<PhonelinkSetup />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 4. ENVIO EM MASSA (CAMPANHAS) */}
      {showCampaigns && hasPermission("campaigns.view") && (
        <>
          <Tooltip title={collapsed ? i18n.t("mainDrawer.listItems.campaigns") : ""} placement="right">
            <ListItem
              dense
              button
              onClick={(e) => {
                e.stopPropagation();
                setOpenCampaignSubmenu((prev) => !prev);
              }}
              onMouseEnter={() => setCampaignHover(true)}
              onMouseLeave={() => setCampaignHover(false)}
              className={clsx(classes.listItem, viewMode === "modern" && classes.modernItem, (isCampaignRouteActive || openCampaignSubmenu) && "active")}
            >
              <ListItemIcon className={clsx(viewMode === "modern" && classes.modernIcon)}>
                {viewMode === "modern" ? (
                  <EventAvailableIcon />
                ) : (
                  <Avatar
                    className={`${classes.iconHoverActive} ${isCampaignRouteActive || campaignHover ? "active" : ""
                      }`}
                  >
                    <EventAvailableIcon />
                  </Avatar>
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={clsx(classes.listItemText, viewMode === "modern" && classes.modernText)}>
                    {i18n.t("mainDrawer.listItems.campaigns")}
                  </Typography>
                }
              />
              {openCampaignSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItem>
          </Tooltip>
          <Collapse
            in={openCampaignSubmenu}
            timeout="auto"
            unmountOnExit
            style={{
              backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
            }}
          >
            <List dense component="div" disablePadding>
              <ListItemLink
                to="/campaigns"
                primary={i18n.t("campaigns.subMenus.list")}
                icon={<ListIcon />}
                viewMode={viewMode}
                tooltip={collapsed}
              />
              <ListItemLink
                to="/contact-lists"
                primary={i18n.t("campaigns.subMenus.listContacts")}
                icon={<PeopleIcon />}
                viewMode={viewMode}
                tooltip={collapsed}
              />
              {hasPermission("contacts.import") && (
                <ListItemLink
                  to="/leads-import"
                  primary="Importar Leads"
                  icon={<LeadsImportIcon />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
              {hasPermission("contacts.import") && (
                <ListItemLink
                  to="/lead-scraper"
                  primary="Captador de Leads"
                  icon={<LeadScraperIcon />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
              <ListItemLink
                to="/campaigns-config"
                primary={i18n.t("campaigns.subMenus.settings")}
                icon={<SettingsOutlinedIcon />}
                viewMode={viewMode}
                tooltip={collapsed}
              />
              {hasPermission("email-campaigns.view") && (
                <ListItemLink
                  to="/email-campaigns"
                  primary="Campanhas de E-mail"
                  icon={<EventAvailableIcon />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
              {hasPermission("drip-sequences.view") && (
                <ListItemLink
                  to="/drip-sequences"
                  primary="Sequências de Drip"
                  icon={<EventAvailableIcon />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* 5. IA (INTELIGÊNCIA ARTIFICIAL) */}
      {showOpenAi && hasAnyPermission(["ai-agents.view", "prompts.view", "ai-settings.view", "ai-training.view", "files.view"]) && (
        <>
          <Tooltip title={collapsed ? "Inteligência Artificial" : ""} placement="right">
            <ListItem
              dense
              button
              onClick={(e) => {
                e.stopPropagation();
                setOpenIASubmenu((prev) => !prev);
              }}
              onMouseEnter={() => setIaHover(true)}
              onMouseLeave={() => setIaHover(false)}
              className={clsx(classes.listItem, viewMode === "modern" && classes.modernItem, (isIARouteActive || openIASubmenu) && "active")}
            >
              <ListItemIcon className={clsx(viewMode === "modern" && classes.modernIcon)}>
                {viewMode === "modern" ? (
                  <SmartToy />
                ) : (
                  <Avatar
                    className={`${classes.iconHoverActive} ${isIARouteActive || iaHover ? "active" : ""
                      }`}
                  >
                    <SmartToy />
                  </Avatar>
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={clsx(classes.listItemText, viewMode === "modern" && classes.modernText)}>
                    Inteligência Artificial
                  </Typography>
                }
              />
              {openIASubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItem>
          </Tooltip>

          <Collapse
            in={openIASubmenu}
            timeout="auto"
            unmountOnExit
            style={{
              backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
            }}
          >
            <List dense component="div" disablePadding>
              {hasPermission("ai-agents.view") && (
                <ListItemLink
                  to="/ai-agents"
                  primary="⭐ Agentes de IA"
                  icon={<SmartToy />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
              {hasPermission("ai-training.view") && (
                <ListItemLink
                  to="/ai-training"
                  primary="🧪 Training / Sandbox"
                  icon={<SmartToy />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
              {hasPermission("ai-settings.view") && (
                <ListItemLink
                  to="/ai-settings"
                  primary="Configurações IA"
                  icon={<Memory />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
              {hasPermission("files.view") && (
                <ListItemLink
                  to="/files"
                  primary="Base de Conhecimento"
                  icon={<AttachFile />}
                  viewMode={viewMode}
                  tooltip={collapsed}
                />
              )}
              <ListItemLink
                to="/knowledge-base"
                primary="Base de Conhecimento IA"
                icon={<KnowledgeBaseIcon />}
                viewMode={viewMode}
                tooltip={collapsed}
              />
            </List>
          </Collapse>
        </>
      )}

      {/* 6. FLOWBUILDER */}
      {hasPermission("flowbuilder.view") && (
        <>
          <Tooltip title={collapsed ? i18n.t("mainDrawer.listItems.flowbuilder") : ""} placement="right">
            <ListItem
              dense
              button
              onClick={(e) => {
                e.stopPropagation();
                setOpenFlowSubmenu((prev) => !prev);
              }}
              onMouseEnter={() => setFlowHover(true)}
              onMouseLeave={() => setFlowHover(false)}
              className={clsx(classes.listItem, viewMode === "modern" && classes.modernItem, (isFlowbuilderRouteActive || openFlowSubmenu) && "active")}
            >
              <ListItemIcon className={clsx(viewMode === "modern" && classes.modernIcon)}>
                {viewMode === "modern" ? (
                  <Webhook />
                ) : (
                  <Avatar
                    className={`${classes.iconHoverActive} ${isFlowbuilderRouteActive || flowHover ? "active" : ""
                      }`}
                  >
                    <Webhook />
                  </Avatar>
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={clsx(classes.listItemText, viewMode === "modern" && classes.modernText)}>
                    {i18n.t("mainDrawer.listItems.flowbuilder")}
                  </Typography>
                }
              />
              {openFlowSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItem>
          </Tooltip>

          <Collapse
            in={openFlowSubmenu}
            timeout="auto"
            unmountOnExit
            style={{
              backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
            }}
          >
            <List dense component="div" disablePadding>
              <ListItemLink
                to="/phrase-lists"
                primary={i18n.t("flowbuilder.subMenus.campaign")}
                icon={<EventAvailableIcon />}
                viewMode={viewMode}
                tooltip={collapsed}
              />

              <ListItemLink
                to="/flowbuilders"
                primary={i18n.t("flowbuilder.subMenus.conversation")}
                icon={<ShapeLine />}
                viewMode={viewMode}
                tooltip={collapsed}
              />
            </List>
          </Collapse>
        </>
      )}

      {/* 7. FILAS E CHATBOTS */}
      {hasPermission("queues.view") && (
        <ListItemLink
          to="/queues"
          primary={i18n.t("mainDrawer.listItems.queues")}
          icon={<AccountTreeOutlinedIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 8. INFORMATIVOS */}
      {user.super && (
        <ListItemLink
          to="/announcements"
          primary={i18n.t("mainDrawer.listItems.annoucements")}
          icon={<AnnouncementIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 9. API */}
      {showExternalApi && hasPermission("external-api.view") && (
        <ListItemLink
          to="/messages-api"
          primary={i18n.t("mainDrawer.listItems.messagesAPI")}
          icon={<CodeRoundedIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 10. INTEGRAÇÕES */}
      {showIntegrations && hasPermission("integrations.view") && (
        <ListItemLink
          to="/queue-integration"
          primary={i18n.t("mainDrawer.listItems.queueIntegration")}
          icon={<DeviceHubOutlined />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 11. FINANCEIRO */}
      {hasPermission("financeiro.view") && (
        <ListItemLink
          to="/financeiro"
          primary={i18n.t("mainDrawer.listItems.financeiro")}
          icon={<LocalAtmIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 12. CONFIGURAÇÕES */}
      {hasPermission("settings.view") && (
        <ListItemLink
          to="/settings"
          primary={i18n.t("mainDrawer.listItems.settings")}
          icon={<SettingsOutlinedIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {/* 13. EMPRESAS (SUPER) */}
      {user.super && (
        <ListItemLink
          to="/companies"
          primary={i18n.t("mainDrawer.listItems.companies")}
          icon={<BusinessIcon />}
          viewMode={viewMode}
          tooltip={collapsed}
        />
      )}

      {!collapsed && (
        <React.Fragment>
          <Divider />
          <Tooltip title={`BACKEND BUILD: ${versionInfo.buildDate} | Commit: ${versionInfo.commitShort || versionInfo.commit} | Frontend: ${versionInfo.frontend}`}>
            <Typography
              style={{
                fontSize: "12px",
                padding: "10px",
                textAlign: "center",
                fontWeight: "bold",
                cursor: "default",
              }}
            >
              {`Versão ${versionInfo.backend}`}
            </Typography>
          </Tooltip>
        </React.Fragment>
      )}
    </div>
  );
};

export default MainListItems;
